howspace.components.workspacePicker = (() => {
    'use strict';

    // The picker follows the ARIA combobox pattern. Focus stays in the search input, and the input points at the
    // active option with aria-activedescendant, so typing and choosing happen in one place.

    const getSearchInput = workspacePicker => workspacePicker.find('.howspace-form-search-input');

    const getOptions = workspacePicker => workspacePicker.find('[role="listbox"] [role="option"]');

    const isExpanded = workspacePicker => getSearchInput(workspacePicker).attr('aria-expanded') === 'true';

    const setActiveOption = (workspacePicker, option) => {
        const workspaceSearchInput = getSearchInput(workspacePicker);
        getOptions(workspacePicker).removeClass('active').attr('aria-selected', 'false');

        if (option === null) {
            workspaceSearchInput.removeAttr('aria-activedescendant');
            return;
        }

        option.addClass('active').attr('aria-selected', 'true');
        workspaceSearchInput.attr('aria-activedescendant', option.attr('id'));
        option[0].scrollIntoView({ block: 'nearest' });
    };

    const openList = workspacePicker => {
        workspacePicker.find('.howspace-menu').removeClass('d-none');
        howspace.components.dropdown.open(workspacePicker.find('.dropdown'), false);
    };

    // Screen readers only read a status when its text changes, so it is emptied first and an empty result twice in
    // a row is read out both times.
    const announce = (workspacePicker, text) => {
        const status = workspacePicker.find('[role="status"]').text('');
        clearTimeout(status.data('announce-timeout-id'));
        if (text !== '') {
            status.data('announce-timeout-id', setTimeout(() => status.text(text), 100));
        }
    };

    const closeList = workspacePicker => {
        announce(workspacePicker, '');
        setActiveOption(workspacePicker, null);
        if (isExpanded(workspacePicker)) {
            howspace.components.dropdown.close(workspacePicker.find('.dropdown'));
        }
    };

    const searchWorkspaces = e => {
        const workspaceSearchInput = $(e.currentTarget);
        const workspacePicker = workspaceSearchInput.closest('.howspace-workspace-picker');
        const search = e.detail.value;

        // A chosen workspace is shown read-only, and Enter on it must not search for its name.
        if (workspaceSearchInput.prop('readonly')) {
            return;
        }

        if (search === '' && workspaceSearchInput[0].dataset.allowEmpty !== 'true') {
            closeList(workspacePicker);
            return;
        }

        real.ajax.json(real.baseUri + workspacePicker.data('search-url') + '?search=' + encodeURIComponent(search) + '&sort_by=name', {
            type: 'GET',
            success: response => {
                if (response.search !== undefined && response.search !== workspaceSearchInput.val().trim()) {
                    // discard the results since the search input value has changed since the request was sent
                    return;
                }

                const html = response.table_html.trim();
                const listbox = workspacePicker.find('[role="listbox"]');
                listbox.html(html).toggleClass('hidden', html === '');
                listbox.find('[role="option"]').each((index, option) => {
                    option.id = `${listbox.attr('id')}-option-${index}`;
                });

                // Focus stays in the input, so an empty result is also told to screen readers through the status.
                const emptyText = html === '' ? howspace.translate('common_no_workspaces_were_found') : '';
                workspacePicker.find('.workspace-picker-empty').text(emptyText).toggleClass('hidden', html !== '');

                setActiveOption(workspacePicker, null);
                // Results that arrive after focus has moved on must not open a list nobody is in.
                if (document.activeElement === workspaceSearchInput[0]) {
                    openList(workspacePicker);
                    announce(workspacePicker, emptyText);
                }
            }
        });
    };

    const selectWorkspace = (workspacePicker, option) => {
        const workspaceSearchInput = getSearchInput(workspacePicker);

        workspaceSearchInput.val(option.data('workspace-name'));
        workspaceSearchInput.prop('readonly', true);
        workspacePicker.find('input[type="hidden"]').val(option.data('workspace-id'));

        workspaceSearchInput.closest('.howspace-form-search-label').addClass('show-clear-search-btn')
            .find('.clear-search-btn').removeAttr('tabindex');

        closeList(workspacePicker);
        workspaceSearchInput.trigger('focus');
    };

    const handleOptionClick = e => {
        const option = $(e.currentTarget);
        selectWorkspace(option.closest('.howspace-workspace-picker'), option);
    };

    // dropdown.js handles these keys on the body for menus whose items take focus. Here they edit the text or
    // move the active option, so they stop at the input.
    const INPUT_KEYS = [' ', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', 'Tab'];

    const handleKeyDown = e => {
        if (INPUT_KEYS.includes(e.key)) {
            e.stopPropagation();
        }

        const workspaceSearchInput = $(e.currentTarget);
        const workspacePicker = workspaceSearchInput.closest('.howspace-workspace-picker');
        const options = getOptions(workspacePicker);
        const activeIndex = options.index(options.filter('.active'));

        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                // The options of a chosen workspace are those of an earlier search. Clearing it starts a new one.
                if (workspaceSearchInput.prop('readonly') || options.length === 0) {
                    return;
                }

                e.preventDefault();
                if (!isExpanded(workspacePicker)) {
                    openList(workspacePicker);
                }

                let nextIndex;
                if (e.key === 'ArrowDown') {
                    nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % options.length;
                } else {
                    nextIndex = activeIndex < 0 ? options.length - 1 : (activeIndex - 1 + options.length) % options.length;
                }
                setActiveOption(workspacePicker, options.eq(nextIndex));
                break;
            }

            case 'Enter':
                if (isExpanded(workspacePicker) && activeIndex >= 0) {
                    e.preventDefault();
                    selectWorkspace(workspacePicker, options.eq(activeIndex));
                }
                break;

            case 'Tab':
                // The list holds no focus, so Tab leaves the picker and the list closes behind it.
                closeList(workspacePicker);
                break;
        }
    };

    const handleBlur = e => {
        setActiveOption($(e.currentTarget).closest('.howspace-workspace-picker'), null);
    };

    const clearWorkspace = e => {
        const btn = $(e.currentTarget);
        const workspacePicker = btn.closest('.howspace-workspace-picker');
        const workspaceSearchInput = getSearchInput(workspacePicker);

        workspaceSearchInput.prop('readonly', false);

        workspacePicker.find('input[type="hidden"]').val('');
    };

    return {
        init: elem => {
            elem.findIncludingSelf('.howspace-workspace-picker:not(.howspace-workspace-picker-initialized)').each(function () {
                const workspacePicker = $(this);
                workspacePicker.addClass('howspace-workspace-picker-initialized');
                const workspaceSearchInput = getSearchInput(workspacePicker);
                workspacePicker.find('[role="listbox"]').attr('aria-labelledby', workspaceSearchInput.attr('aria-labelledby'));
                workspacePicker.find('.dropdown').on('click', '[role="option"]', handleOptionClick);
                workspaceSearchInput.on(howspace.components.searchInput.SEARCH, searchWorkspaces);
                workspaceSearchInput.on('keydown', handleKeyDown);
                workspaceSearchInput.on('blur', handleBlur);
                workspacePicker.find('.clear-search-btn').on('click', clearWorkspace);
            });
        },
        destroy: elem => {
            elem.findIncludingSelf('.howspace-workspace-picker.howspace-workspace-picker-initialized').each(function () {
                const workspacePicker = $(this);
                workspacePicker.removeClass('howspace-workspace-picker-initialized');
                const workspaceSearchInput = getSearchInput(workspacePicker);
                workspacePicker.find('.dropdown').off('click', '[role="option"]', handleOptionClick);
                workspaceSearchInput.off(howspace.components.searchInput.SEARCH, searchWorkspaces);
                workspaceSearchInput.off('keydown', handleKeyDown);
                workspaceSearchInput.off('blur', handleBlur);
                workspacePicker.find('.clear-search-btn').off('click', clearWorkspace);
            });
        }
    };
})();
